import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    IoEyeOutline, IoSearchOutline, IoChevronBack, IoChevronForward,
    IoArrowUp, IoArrowDown, IoTime, IoAddOutline, IoCreateOutline,
    IoPowerOutline, IoCloseOutline,
} from 'react-icons/io5';
import { API_URL } from '../../config';
import './Employees.css';

const emptyForm = {
    first_name: '',
    last_name: '',
    emp_code: '',
    email: '',
    birth_date: '',
    date_of_joining: '',
    designation: '',
    department: '',
    vendor_type: '',
    address: '',
    city: '',
    pincode: '',
    state_id: '',
    pan_no: '',
    blood_group: '',
    sex: '',
    current_salary: '',
    reporting_manager_id: '',
    role_id: '',
};

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken')}`,
});

const Employees = () => {
    const [employees, setEmployees] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [sortOrder, setSortOrder] = useState('recent');
    const [lookups, setLookups] = useState({
        departments: [],
        designations: [],
        vendorTypes: [],
        states: [],
        bloodGroups: [],
        sexes: [],
        roles: [],
    });
    const [modal, setModal] = useState(null); // 'create' | 'edit' | 'view'
    const [formData, setFormData] = useState(emptyForm);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchEmployees = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/hr/employees?page=${page}&limit=1000`, {
                headers: authHeaders(),
            });
            const data = res.data.data || (Array.isArray(res.data) ? res.data : []);
            const meta = res.data.pagination || {
                currentPage: page,
                totalPages: Math.ceil((data.length || 0) / 1000) || 1,
                totalRecords: data.length || 0,
            };
            setEmployees(data);
            setPagination(meta);
            setError(null);
        } catch (err) {
            console.error('Error fetching employees:', err);
            setError('Failed to load employee directory.');
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchLookups = useCallback(async () => {
        const base = API_URL.includes('/api') ? API_URL.replace(/\/api\/?$/, '') : API_URL;
        const getCol = async (table, column) => {
            try {
                const res = await axios.get(`${base}/api/get-column/${table}/${column}`, {
                    headers: authHeaders(),
                });
                return res.data?.data || res.data || [];
            } catch {
                return [];
            }
        };
        const [departments, designations, vendorTypes, states, bloodGroups, sexes] = await Promise.all([
            getCol('departments', 'department_name'),
            getCol('designation', 'designation_name'),
            getCol('common_master', 'vendor_type'),
            getCol('state_master', 'state_name'),
            getCol('common_master', 'blood_group'),
            getCol('common_master', 'sex'),
        ]);
        setLookups({
            departments,
            designations,
            vendorTypes,
            states,
            bloodGroups,
            sexes,
            roles: [],
        });
    }, []);

    useEffect(() => {
        fetchEmployees(1);
        fetchLookups();
    }, [fetchEmployees, fetchLookups]);

    const filteredEmployees = useMemo(() => {
        let result = employees.filter((emp) =>
            (`${emp.first_name} ${emp.last_name}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.emp_code || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (sortOrder === 'asc') {
            result = [...result].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
        } else if (sortOrder === 'desc') {
            result = [...result].sort((a, b) => (b.first_name || '').localeCompare(a.first_name || ''));
        } else {
            result = [...result].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        return result;
    }, [employees, searchTerm, sortOrder]);

    const openCreate = () => {
        const employeeVendor = lookups.vendorTypes.find(
            (v) => String(v.value || v.vendor_type || '').toLowerCase() === 'employee'
        );
        setFormData({
            ...emptyForm,
            date_of_joining: new Date().toISOString().slice(0, 10),
            vendor_type: employeeVendor?.id || '',
        });
        setSelected(null);
        setModal('create');
    };

    const openEdit = async (emp) => {
        try {
            const res = await axios.get(`${API_URL}/hr/employees/${emp.id}`, { headers: authHeaders() });
            const d = res.data;
            setSelected(d);
            setFormData({
                first_name: d.first_name || '',
                last_name: d.last_name || '',
                emp_code: d.emp_code || '',
                email: d.email || '',
                birth_date: d.birth_date
                    ? (d.birth_date.includes('-') && d.birth_date.length === 10 && d.birth_date[4] === '-'
                        ? d.birth_date
                        : d.birth_date.split('-').reverse().join('-'))
                    : '',
                date_of_joining: d.date_of_joining || '',
                designation: d.designation_id || '',
                department: d.department_id || '',
                vendor_type: d.vendor_type || '',
                address: d.address || '',
                city: d.city || '',
                pincode: d.pincode || '',
                state_id: d.state_id || '',
                pan_no: d.pan_no || '',
                blood_group: d.blood_group || '',
                sex: d.sex || '',
                current_salary: d.current_salary || '',
                reporting_manager_id: d.reporting_manager_id || '',
                role_id: d.role_id || '',
            });
            setModal('edit');
        } catch (e) {
            showToast('error', 'Failed to load employee');
        }
    };

    const openView = async (emp) => {
        try {
            const res = await axios.get(`${API_URL}/hr/employees/${emp.id}`, { headers: authHeaders() });
            setSelected(res.data);
            setModal('view');
        } catch (e) {
            showToast('error', 'Failed to load employee');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                designation: formData.designation || null,
                department: formData.department || null,
                reporting_manager_id: formData.reporting_manager_id || null,
                role_id: formData.role_id || null,
            };
            if (modal === 'create') {
                await axios.post(`${API_URL}/hr/employees`, payload, {
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                });
                showToast('success', 'Employee created');
            } else {
                await axios.put(`${API_URL}/hr/employees/${selected.id}`, payload, {
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                });
                showToast('success', 'Employee updated');
            }
            setModal(null);
            fetchEmployees(pagination.currentPage);
        } catch (err) {
            showToast('error', err.response?.data?.error || err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (emp) => {
        const action = emp.active ? 'deactivate' : 'reactivate';
        if (!window.confirm(`${action === 'deactivate' ? 'Deactivate' : 'Reactivate'} ${emp.first_name}?`)) return;
        try {
            await axios.put(`${API_URL}/hr/employees/${emp.id}/${action}`, {}, { headers: authHeaders() });
            showToast('success', `User ${action}d`);
            fetchEmployees(pagination.currentPage);
        } catch (err) {
            showToast('error', err.response?.data?.error || `Failed to ${action}`);
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'status-indicator';
        const s = status.toLowerCase();
        if (s.includes('active')) return 'status-indicator status-active';
        return 'status-indicator status-inactive';
    };

    const optionId = (row) => row.id ?? row.department_id ?? row.designation_id ?? row.state_id;
    const optionLabel = (row) =>
        row.value || row.department_name || row.designation_name || row.state_name || row.vendor_type || row.blood_group || row.sex || `#${optionId(row)}`;

    if (loading && employees.length === 0) {
        return (
            <div className="employees-page">
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading Directory...</div>
            </div>
        );
    }

    return (
        <div className="employees-page">
            {toast && (
                <div style={{
                    position: 'fixed', top: 16, right: 16, zIndex: 9999,
                    padding: '10px 16px', borderRadius: 8,
                    background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
                    color: toast.type === 'error' ? '#991b1b' : '#166534',
                }}>
                    {toast.message}
                </div>
            )}

            <div className="page-header">
                <div className="page-title">
                    <h1>Employee Directory</h1>
                    <div className="page-sub">{pagination.totalRecords} Employees</div>
                </div>
                <div className="controls-section">
                    <div className="search-bar">
                        <IoSearchOutline className="search-icon" size={18} />
                        <input
                            type="text"
                            placeholder="Search by name, code or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <div className="sort-controls">
                        <span className="sort-label">Sort:</span>
                        <div className="sort-buttons">
                            <button className={`sort-btn ${sortOrder === 'recent' ? 'active' : ''}`} onClick={() => setSortOrder('recent')}>
                                <IoTime size={14} /><span>Recent</span>
                            </button>
                            <button className={`sort-btn ${sortOrder === 'asc' ? 'active' : ''}`} onClick={() => setSortOrder('asc')}>
                                <IoArrowUp size={14} /><span>A-Z</span>
                            </button>
                            <button className={`sort-btn ${sortOrder === 'desc' ? 'active' : ''}`} onClick={() => setSortOrder('desc')}>
                                <IoArrowDown size={14} /><span>Z-A</span>
                            </button>
                        </div>
                    </div>
                    <button
                        className="sort-btn"
                        onClick={async () => {
                            try {
                                const res = await axios.get(`${API_URL}/hr/employees/export`, {
                                    headers: authHeaders(),
                                    responseType: 'blob',
                                });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'employees_export.csv';
                                a.click();
                                window.URL.revokeObjectURL(url);
                            } catch (e) {
                                showToast('error', 'Export failed');
                            }
                        }}
                    >
                        Export CSV
                    </button>
                    <button className="sort-btn active" onClick={openCreate} style={{ gap: 6 }}>
                        <IoAddOutline size={16} /> Add Employee
                    </button>
                </div>
            </div>

            {error ? (
                <div style={{ padding: '20px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px' }}>{error}</div>
            ) : (
                <div className="table-container">
                    <table className="emp-table">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>ID Code</th>
                                <th>Employee Profile</th>
                                <th>Designation</th>
                                <th>Department</th>
                                <th>Manager</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEmployees.length > 0 ? filteredEmployees.map((emp) => (
                                <tr key={emp.id}>
                                    <td style={{ fontWeight: 600, color: '#64748b' }}>{emp.emp_code || '#--'}</td>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">{emp.first_name ? emp.first_name[0].toUpperCase() : 'U'}</div>
                                            <div className="user-info">
                                                <div className="user-name">{emp.first_name} {emp.last_name}</div>
                                                <div className="user-email">{emp.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className="role-badge">{emp.designation_name || 'N/A'}</span></td>
                                    <td>{emp.department_name || 'General'}</td>
                                    <td>{emp.reporting_manager_name || '—'}</td>
                                    <td>
                                        <span className={getStatusColor(emp.status_name || (emp.active ? 'Active' : 'Inactive'))}>
                                            {emp.status_name || (emp.active ? 'Active' : 'Inactive')}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button className="action-btn" title="View" onClick={() => openView(emp)}><IoEyeOutline size={18} /></button>
                                        <button className="action-btn" title="Edit" onClick={() => openEdit(emp)}><IoCreateOutline size={18} /></button>
                                        <button className="action-btn" title={emp.active ? 'Deactivate' : 'Reactivate'} onClick={() => toggleActive(emp)}>
                                            <IoPowerOutline size={18} />
                                        </button>
                                        <button
                                            className="action-btn"
                                            title="Resend invite"
                                            onClick={async () => {
                                                try {
                                                    const res = await axios.post(
                                                        `${API_URL}/auth/invite`,
                                                        { userId: emp.id },
                                                        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
                                                    );
                                                    showToast('success', res.data?.message || 'Invite sent');
                                                } catch (e) {
                                                    showToast('error', e.response?.data?.error || 'Invite failed');
                                                }
                                            }}
                                        >
                                            Invite
                                        </button>
                                        <Link className="action-btn" title="Onboarding docs" to="/onboarding" style={{ display: 'inline-flex' }}>
                                            Docs
                                        </Link>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No employees found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className="pagination-footer">
                        <div className="pagination-info">Showing Page {pagination.currentPage} of {pagination.totalPages}</div>
                        <div className="pagination-controls">
                            <button className="page-btn" disabled={pagination.currentPage === 1} onClick={() => fetchEmployees(pagination.currentPage - 1)}>
                                <IoChevronBack />
                            </button>
                            <button className="page-btn" disabled={pagination.currentPage === pagination.totalPages} onClick={() => fetchEmployees(pagination.currentPage + 1)}>
                                <IoChevronForward />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {(modal === 'create' || modal === 'edit') && (
                <div className="emp-modal-backdrop" onClick={() => setModal(null)}>
                    <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="emp-modal-header">
                            <h2>{modal === 'create' ? 'Add Employee' : 'Edit Employee'}</h2>
                            <button type="button" className="action-btn" onClick={() => setModal(null)}><IoCloseOutline size={22} /></button>
                        </div>
                        <form onSubmit={handleSave} className="emp-modal-form">
                            <div className="emp-form-grid">
                                <label>First name<input required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} /></label>
                                <label>Last name<input required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} /></label>
                                <label>Emp code<input required disabled={modal === 'edit'} value={formData.emp_code} onChange={(e) => setFormData({ ...formData, emp_code: e.target.value })} /></label>
                                <label>Email<input required type="email" disabled={modal === 'edit'} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></label>
                                <label>Date of joining<input type="date" value={formData.date_of_joining} onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })} /></label>
                                <label>Birth date<input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} /></label>
                                <label>Department
                                    <select value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })}>
                                        <option value="">Select</option>
                                        {lookups.departments.map((d) => <option key={optionId(d)} value={optionId(d)}>{optionLabel(d)}</option>)}
                                    </select>
                                </label>
                                <label>Designation
                                    <select value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })}>
                                        <option value="">Select</option>
                                        {lookups.designations.map((d) => <option key={optionId(d)} value={optionId(d)}>{optionLabel(d)}</option>)}
                                    </select>
                                </label>
                                <label>Reporting manager
                                    <select value={formData.reporting_manager_id} onChange={(e) => setFormData({ ...formData, reporting_manager_id: e.target.value })}>
                                        <option value="">None</option>
                                        {employees.filter((e) => e.active && e.id !== selected?.id).map((e) => (
                                            <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.emp_code})</option>
                                        ))}
                                    </select>
                                </label>
                                {modal === 'create' && (
                                    <label>Vendor type
                                        <select required value={formData.vendor_type} onChange={(e) => setFormData({ ...formData, vendor_type: e.target.value })}>
                                            <option value="">Select</option>
                                            {lookups.vendorTypes.map((d) => <option key={optionId(d)} value={optionId(d)}>{optionLabel(d)}</option>)}
                                        </select>
                                    </label>
                                )}
                                <label>City<input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></label>
                                <label>Address<input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></label>
                                <label>PAN<input value={formData.pan_no} onChange={(e) => setFormData({ ...formData, pan_no: e.target.value })} /></label>
                                <label>Current salary<input type="number" value={formData.current_salary} onChange={(e) => setFormData({ ...formData, current_salary: e.target.value })} /></label>
                            </div>
                            <div className="emp-modal-actions">
                                <button type="button" className="sort-btn" onClick={() => setModal(null)}>Cancel</button>
                                <button type="submit" className="sort-btn active" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {modal === 'view' && selected && (
                <div className="emp-modal-backdrop" onClick={() => setModal(null)}>
                    <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="emp-modal-header">
                            <h2>{selected.first_name} {selected.last_name}</h2>
                            <button type="button" className="action-btn" onClick={() => setModal(null)}><IoCloseOutline size={22} /></button>
                        </div>
                        <div className="emp-form-grid" style={{ padding: '0 4px 16px' }}>
                            <div><strong>Code</strong><div>{selected.emp_code}</div></div>
                            <div><strong>Email</strong><div>{selected.email}</div></div>
                            <div><strong>DOJ</strong><div>{selected.date_of_joining || '—'}</div></div>
                            <div><strong>Dept</strong><div>{selected.department_name || '—'}</div></div>
                            <div><strong>Designation</strong><div>{selected.designation_name || '—'}</div></div>
                            <div><strong>Manager</strong><div>{selected.reporting_manager_name || '—'}</div></div>
                            <div><strong>Status</strong><div>{selected.status_name || (selected.active ? 'Active' : 'Inactive')}</div></div>
                            <div><strong>Contact</strong><div>{selected.contact_details || '—'}</div></div>
                        </div>
                        <div className="emp-modal-actions">
                            <button type="button" className="sort-btn active" onClick={() => { setModal(null); openEdit(selected); }}>Edit</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Employees;
